import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import SellerLayout from "@/components/SellerLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Users, Plus, Edit, Trash2, Mail, Shield, UserCheck, UserX } from "lucide-react";

// Sentinel for the custom-role select ("no custom role" -> role_id null on assign)
const SYSTEM_DEFAULT_ROLE = "system_default";

const UsersManagement = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  const [teamRoles, setTeamRoles] = useState([]);
  const [customRoleId, setCustomRoleId] = useState(SYSTEM_DEFAULT_ROLE);

  // Form states
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("seller_user");
  const [isActive, setIsActive] = useState(true);

  // Check if user is seller_admin
  useEffect(() => {
    if (user.role !== 'seller_admin') {
      toast.error("Only Seller Admins can access user management");
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchUsers();
    // Team-role management (and assignment) is hard-locked to seller_admin
    if (user?.role === 'seller_admin') {
      fetchTeamRoles();
    }
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/seller/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error("Failed to fetch users");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamRoles = async () => {
    try {
      const response = await axios.get(`${API}/seller-roles`);
      const list = Array.isArray(response.data) ? response.data : response.data?.roles || [];
      setTeamRoles(list.filter((r) => !r.is_system));
    } catch (error) {
      console.error("Failed to fetch team roles:", error);
    }
  };

  const handleAssignCustomRole = async (value) => {
    const previous = customRoleId;
    setCustomRoleId(value);
    try {
      await axios.post(`${API}/seller-roles/assign`, {
        user_id: editingUser.id,
        role_id: value === SYSTEM_DEFAULT_ROLE ? null : value
      });
      toast.success(value === SYSTEM_DEFAULT_ROLE
        ? "Custom role cleared - system default permissions apply"
        : "Custom role assigned");
      fetchUsers();
    } catch (error) {
      setCustomRoleId(previous);
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to update custom role");
    }
  };

  const handleOpenDialog = (userToEdit = null) => {
    setCustomRoleId(userToEdit?.role_id || SYSTEM_DEFAULT_ROLE);
    if (userToEdit) {
      setEditingUser(userToEdit);
      setName(userToEdit.name);
      setEmail(userToEdit.email);
      setRole(userToEdit.role);
      setIsActive(userToEdit.is_active);
      setPassword("");
    } else {
      setEditingUser(null);
      setName("");
      setEmail("");
      setPassword("");
      setRole("seller_user");
      setIsActive(true);
    }
    setShowDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setEditingUser(null);
    setName("");
    setEmail("");
    setPassword("");
    setRole("seller_user");
    setIsActive(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name || !email || (!editingUser && !password)) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const userData = {
        name,
        email,
        role,
        is_active: isActive
      };

      if (password) {
        userData.password = password;
      }

      if (editingUser) {
        await axios.put(`${API}/seller/users/${editingUser.id}`, userData);
        toast.success("User updated successfully");
      } else {
        userData.password = password;
        await axios.post(`${API}/seller/users`, userData);
        toast.success("User created successfully");
      }

      handleCloseDialog();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save user");
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/seller/users/${deletingUser.id}`);
      toast.success("User deleted successfully");
      setShowDeleteDialog(false);
      setDeletingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const getRoleBadgeColor = (role) => {
    return role === 'seller_admin' 
      ? 'bg-purple-100 text-purple-800' 
      : 'bg-blue-100 text-blue-800';
  };

  const totalAdmins = users.filter(u => u.role === 'seller_admin').length;
  const totalUsers = users.filter(u => u.role === 'seller_user').length;
  const activeUsers = users.filter(u => u.is_active).length;

  return (
    <SellerLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-slate-800 mb-2">User Management</h1>
            <p className="text-slate-600">Manage users for your account</p>
          </div>
          <Button
            style={{ backgroundColor: '#00A699' }}
            onClick={() => handleOpenDialog()}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add User
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="border-0 shadow-lg">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Total Users</p>
                  <p className="text-3xl font-bold text-slate-800">{users.length}</p>
                </div>
                <Users className="w-10 h-10 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Admins / Users</p>
                  <p className="text-3xl font-bold text-slate-800">{totalAdmins} / {totalUsers}</p>
                </div>
                <Shield className="w-10 h-10 text-purple-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Active Users</p>
                  <p className="text-3xl font-bold text-slate-800">{activeUsers}</p>
                </div>
                <UserCheck className="w-10 h-10 text-green-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Users Table */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle>All Users</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-8 text-slate-600">Loading users...</div>
            ) : users.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-600 mb-4">No users found</p>
                <Button onClick={() => handleOpenDialog()}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First User
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Role</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {users.map((u) => (
                      <tr key={u.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 align-middle">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 bg-slate-200 rounded-full flex items-center justify-center">
                              <span className="text-slate-600 font-semibold">{u.name.charAt(0).toUpperCase()}</span>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-slate-900">{u.name}</div>
                              {u.id === user.id && (
                                <div className="text-xs text-slate-500">(You)</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 align-middle">
                          <div className="flex items-center text-sm text-slate-600">
                            <Mail className="w-4 h-4 mr-2" />
                            {u.email}
                          </div>
                        </td>
                        <td className="px-4 py-3 align-middle">
                          <Badge className={getRoleBadgeColor(u.role)}>
                            {u.role === 'seller_admin' ? 'Admin' : 'User'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 align-middle">
                          {u.is_active ? (
                            <Badge className="bg-green-100 text-green-800">
                              <UserCheck className="w-3 h-3 mr-1" />
                              Active
                            </Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-800">
                              <UserX className="w-3 h-3 mr-1" />
                              Inactive
                            </Badge>
                          )}
                        </td>
                        <td className="px-4 py-3 align-middle">
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleOpenDialog(u)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            {u.id !== user.id && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => {
                                  setDeletingUser(u);
                                  setShowDeleteDialog(true);
                                }}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add/Edit User Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingUser ? 'Edit User' : 'Add New User'}</DialogTitle>
            <DialogDescription>
              {editingUser ? 'Update user information' : 'Create a new user for your account'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 py-4">
            <div>
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Doe"
                required
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="john@example.com"
                required
                disabled={!!editingUser}
                className={`mt-1 ${editingUser ? 'bg-slate-100 cursor-not-allowed' : ''}`}
              />
              {editingUser && (
                <p className="text-xs text-slate-500 mt-1">Email cannot be changed after user creation</p>
              )}
            </div>

            <div>
              <Label htmlFor="password">
                Password {!editingUser && '*'}
                {editingUser && <span className="text-xs text-slate-500"> (Leave blank to keep current)</span>}
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={editingUser ? "Leave blank to keep current" : "Enter password"}
                required={!editingUser}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="role">Role *</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="seller_admin">Seller Admin</SelectItem>
                  <SelectItem value="seller_user">Seller User</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                Admin can manage users, User has view-only access to user management
              </p>
            </div>

            {user?.role === 'seller_admin' && editingUser && role === 'seller_user' && (
              <div>
                <Label htmlFor="custom-role">Custom Role</Label>
                <Select value={customRoleId} onValueChange={handleAssignCustomRole}>
                  <SelectTrigger id="custom-role" className="mt-1" data-testid="custom-role-select">
                    <SelectValue placeholder="System default" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={SYSTEM_DEFAULT_ROLE} data-testid="custom-role-option-default">
                      System default
                    </SelectItem>
                    {teamRoles.map((r) => (
                      <SelectItem key={r.id} value={r.id} data-testid={`custom-role-option-${r.name}`}>
                        {r.display_name || r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  Applies immediately. A custom role replaces the default permissions for this team
                  member — manage roles under Team Roles.
                </p>
              </div>
            )}

            {editingUser && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="rounded"
                />
                <Label htmlFor="isActive" className="font-normal cursor-pointer">
                  Active User
                </Label>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button type="submit" style={{ backgroundColor: '#00A699' }}>
                {editingUser ? 'Update User' : 'Create User'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{deletingUser?.name}</strong>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
            >
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SellerLayout>
  );
};

export default UsersManagement;
